import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import PageHero from '../../components/feature/PageHero';

interface BetOption {
  name: string;
  prob: string;
  price: string;
  payout: string;
  profit: string;
}

interface MarketCardProps {
  badge: string;
  badgeColor: 'gold' | 'teal' | 'cyan' | 'rose' | 'amber' | 'violet' | 'lime' | 'emerald';
  category: string;
  title: string;
  description: string;
  volume24h: string;
  totalVolume: string;
  options: BetOption[];
}

function MarketCard({ badge, badgeColor, category, title, description, volume24h, totalVolume, options }: MarketCardProps) {
  const [selectedOption, setSelectedOption] = useState(options[0].name);
  const [added, setAdded] = useState(false);

  const badgeClasses: Record<string, string> = {
    gold: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    teal: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    rose: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
    amber: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    lime: 'bg-lime-500/20 text-lime-400 border-lime-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  };

  const selected = options.find(o => o.name === selectedOption) || options[0];

  const handleAdd = () => {
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <div className="bg-white/5 backdrop-blur-md rounded-xl border border-slate-700/80 shadow-xl overflow-hidden hover:border-slate-500/80 hover:shadow-2xl hover:shadow-teal-500/5 transition-all flex flex-col h-full">
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold tracking-wide border ${badgeClasses[badgeColor]}`}>
            {badge}
          </span>
          <span className="text-xs text-slate-500 bg-slate-800/60 px-2 py-1 rounded-full border border-slate-700/50">{category}</span>
        </div>
        <div className="mb-4">
          <h3 className="text-lg font-bold text-white mb-1 leading-tight">{title}</h3>
          <p className="text-sm text-slate-400 line-clamp-2">{description}</p>
        </div>
        <div className="flex items-center gap-3 mb-4 p-2.5 bg-white/5 rounded-lg border border-slate-700/50">
          <div className="flex items-center gap-1.5">
            <i className="ri-line-chart-line text-teal-500 text-sm"></i>
            <span className="text-xs text-slate-400">24h Vol:</span>
            <span className="text-xs font-bold text-teal-500">{volume24h}</span>
          </div>
          <div className="w-px h-4 bg-slate-700"></div>
          <div className="flex items-center gap-1.5">
            <i className="ri-water-flash-line text-emerald-400 text-sm"></i>
            <span className="text-xs text-slate-400">Total:</span>
            <span className="text-xs font-bold text-emerald-400">{totalVolume}</span>
          </div>
        </div>
        <div className="flex-1 bg-white/5 rounded-lg p-3 border border-slate-700/50 mb-4 overflow-y-auto max-h-[200px]">
          <div className="space-y-2">
            {options.map((option, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedOption(option.name)}
                className={`cursor-pointer p-2 rounded border transition-colors flex justify-between items-center ${
                  option.name === selectedOption
                    ? 'bg-teal-500/10 border-teal-500/50'
                    : 'bg-white/5 border-slate-700/80 hover:border-slate-500'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold truncate ${option.name === selectedOption ? 'text-teal-400' : 'text-slate-300'}`}>
                    {option.name}
                  </div>
                  <div className="text-xs text-slate-500">Prob: {option.prob}</div>
                </div>
                <div className="text-right pl-2 flex flex-col items-end">
                  <div className="text-xs text-slate-400">Price: {option.price}</div>
                  <div className="text-xs text-slate-300 font-medium">Payout {option.payout}</div>
                  <div className="text-xs text-teal-500">{option.profit}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-auto pt-3 border-t border-slate-700/50">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-400">Selected</div>
              <div className="text-sm font-bold text-white truncate">{selected.name}</div>
              <div className="text-xs text-slate-400">
                <span>Price: </span>
                <span className="text-white font-semibold">{selected.price}</span>
                <span className="mx-1">•</span>
                <span className="text-teal-500 font-semibold">{selected.payout}</span>
                <span className="text-teal-500 ml-1">{selected.profit}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                className={`inline-flex items-center justify-center text-sm h-9 rounded-md px-4 font-bold shadow-lg cursor-pointer whitespace-nowrap transition-all ${
                  added
                    ? 'bg-emerald-500 text-white shadow-emerald-500/25'
                    : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white shadow-teal-500/25'
                }`}
              >
                {added ? <><i className="ri-check-line mr-1"></i><span>Added</span></> : <><span className="hidden sm:inline">Add to Slip</span><span className="sm:hidden">Add</span></>}
              </button>
              <button className="inline-flex items-center justify-center text-sm font-medium border h-9 rounded-md border-slate-600 hover:bg-slate-800 text-slate-300 px-2 cursor-pointer transition-colors" title="View Details">
                <i className="ri-scales-3-line"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Market Data ────────────────────────────────────────────────────────────

const nflMarkets: MarketCardProps[] = [
  {
    badge: 'Game Winner', badgeColor: 'teal', category: 'NFL Week 18',
    title: 'Chiefs vs. Raiders — Moneyline',
    description: 'Who wins the regular season finale between Kansas City and Las Vegas?',
    volume24h: '$214.3K', totalVolume: '$1.1M',
    options: [
      { name: 'Kansas City Chiefs', prob: '71%', price: '$0.71', payout: '$1.00', profit: '+$0.29' },
      { name: 'Las Vegas Raiders', prob: '29%', price: '$0.29', payout: '$1.00', profit: '+$0.71' },
    ],
  },
  {
    badge: 'Point Spread', badgeColor: 'amber', category: 'NFL Week 18',
    title: 'Chiefs -7.5 vs. Raiders',
    description: 'Will the Chiefs cover the 7.5-point spread against the Raiders?',
    volume24h: '$187.6K', totalVolume: '$890K',
    options: [
      { name: 'Chiefs -7.5 (Cover)', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Raiders +7.5 (Cover)', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
    ],
  },
  {
    badge: 'Player Prop', badgeColor: 'cyan', category: 'Passing Props',
    title: 'Mahomes Passing TDs O/U 2.5',
    description: 'Will Patrick Mahomes throw over or under 2.5 touchdown passes?',
    volume24h: '$143.2K', totalVolume: '$672K',
    options: [
      { name: 'Over 2.5 TDs', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
      { name: 'Under 2.5 TDs', prob: '42%', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
    ],
  },
  {
    badge: 'Season Award', badgeColor: 'gold', category: 'NFL Awards',
    title: 'NFL MVP 2024-25',
    description: 'Which player will be named the NFL Most Valuable Player for the 2024-25 season?',
    volume24h: '$321.8K', totalVolume: '$2.4M',
    options: [
      { name: 'Lamar Jackson (BAL)', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
      { name: 'Patrick Mahomes (KC)', prob: '31%', price: '$0.31', payout: '$1.00', profit: '+$0.69' },
      { name: 'Josh Allen (BUF)', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
      { name: 'Other', prob: '11%', price: '$0.11', payout: '$1.00', profit: '+$0.89' },
    ],
  },
  {
    badge: 'Game Total', badgeColor: 'rose', category: 'Totals',
    title: 'Eagles vs. Cowboys O/U 48.5',
    description: 'Will the combined score of Eagles vs. Cowboys go over or under 48.5?',
    volume24h: '$98.4K', totalVolume: '$441K',
    options: [
      { name: 'Over 48.5', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      { name: 'Under 48.5', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
    ],
  },
  {
    badge: 'Rushing Prop', badgeColor: 'lime', category: 'Player Props',
    title: 'Derrick Henry Rush Yards O/U 88.5',
    description: 'Will Derrick Henry rush for over or under 88.5 yards this week?',
    volume24h: '$76.1K', totalVolume: '$318K',
    options: [
      { name: 'Over 88.5 Yards', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
      { name: 'Under 88.5 Yards', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
    ],
  },
  {
    badge: 'Playoff Seed', badgeColor: 'violet', category: 'NFL Playoffs',
    title: 'NFC #1 Seed',
    description: 'Which NFC team will earn the top playoff seed and home-field advantage?',
    volume24h: '$189.7K', totalVolume: '$1.3M',
    options: [
      { name: 'Philadelphia Eagles', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      { name: 'San Francisco 49ers', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'Detroit Lions', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Other', prob: '9%', price: '$0.09', payout: '$1.00', profit: '+$0.91' },
    ],
  },
  {
    badge: 'Receiving Prop', badgeColor: 'cyan', category: 'Player Props',
    title: 'CeeDee Lamb Rec Yards O/U 82.5',
    description: 'Will CeeDee Lamb record over or under 82.5 receiving yards this week?',
    volume24h: '$64.3K', totalVolume: '$287K',
    options: [
      { name: 'Over 82.5 Yards', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      { name: 'Under 82.5 Yards', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
    ],
  },
];

const nbaMarkets: MarketCardProps[] = [
  {
    badge: 'Game Winner', badgeColor: 'teal', category: 'NBA Regular Season',
    title: 'Lakers vs. Celtics — Moneyline',
    description: 'Who wins the marquee matchup between the Lakers and Celtics?',
    volume24h: '$198.4K', totalVolume: '$980K',
    options: [
      { name: 'Boston Celtics', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
      { name: 'Los Angeles Lakers', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
    ],
  },
  {
    badge: 'Season Award', badgeColor: 'gold', category: 'NBA Awards',
    title: 'NBA MVP 2024-25',
    description: 'Who will win the NBA Most Valuable Player award this season?',
    volume24h: '$287.6K', totalVolume: '$2.1M',
    options: [
      { name: 'Nikola Jokic (DEN)', prob: '39%', price: '$0.39', payout: '$1.00', profit: '+$0.61' },
      { name: 'Shai Gilgeous-Alexander (OKC)', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'Giannis Antetokounmpo (MIL)', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
      { name: 'Luka Doncic (DAL)', prob: '10%', price: '$0.10', payout: '$1.00', profit: '+$0.90' },
      { name: 'Other', prob: '6%', price: '$0.06', payout: '$1.00', profit: '+$0.94' },
    ],
  },
  {
    badge: 'Player Prop', badgeColor: 'cyan', category: 'Scoring Props',
    title: 'LeBron James Points O/U 24.5',
    description: 'Will LeBron James score over or under 24.5 points tonight?',
    volume24h: '$112.3K', totalVolume: '$534K',
    options: [
      { name: 'Over 24.5 Points', prob: '55%', price: '$0.55', payout: '$1.00', profit: '+$0.45' },
      { name: 'Under 24.5 Points', prob: '45%', price: '$0.45', payout: '$1.00', profit: '+$0.55' },
    ],
  },
  {
    badge: 'Championship', badgeColor: 'amber', category: 'NBA Finals',
    title: 'NBA Champion 2024-25',
    description: 'Which team will win the 2024-25 NBA Championship?',
    volume24h: '$412.7K', totalVolume: '$3.2M',
    options: [
      { name: 'Boston Celtics', prob: '31%', price: '$0.31', payout: '$1.00', profit: '+$0.69' },
      { name: 'Oklahoma City Thunder', prob: '22%', price: '$0.22', payout: '$1.00', profit: '+$0.78' },
      { name: 'Denver Nuggets', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
      { name: 'Cleveland Cavaliers', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
    ],
  },
  {
    badge: 'Point Spread', badgeColor: 'rose', category: 'Spread',
    title: 'Thunder -5.5 vs. Nuggets',
    description: 'Will OKC cover the 5.5-point spread against Denver?',
    volume24h: '$87.4K', totalVolume: '$398K',
    options: [
      { name: 'Thunder -5.5 (Cover)', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      { name: 'Nuggets +5.5 (Cover)', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
    ],
  },
  {
    badge: 'Assists Prop', badgeColor: 'violet', category: 'Player Props',
    title: 'Trae Young Assists O/U 9.5',
    description: 'Will Trae Young dish out over or under 9.5 assists tonight?',
    volume24h: '$54.8K', totalVolume: '$231K',
    options: [
      { name: 'Over 9.5 Assists', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      { name: 'Under 9.5 Assists', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
    ],
  },
  {
    badge: 'Rebounds Prop', badgeColor: 'emerald', category: 'Player Props',
    title: 'Jokic Rebounds O/U 12.5',
    description: 'Will Nikola Jokic grab over or under 12.5 rebounds tonight?',
    volume24h: '$67.2K', totalVolume: '$289K',
    options: [
      { name: 'Over 12.5 Rebounds', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      { name: 'Under 12.5 Rebounds', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
    ],
  },
  {
    badge: 'Game Total', badgeColor: 'lime', category: 'Totals',
    title: 'Warriors vs. Suns O/U 224.5',
    description: 'Will the combined score of Warriors vs. Suns go over or under 224.5?',
    volume24h: '$73.1K', totalVolume: '$312K',
    options: [
      { name: 'Over 224.5', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Under 224.5', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
    ],
  },
];

const mlbMarkets: MarketCardProps[] = [
  {
    badge: 'World Series', badgeColor: 'gold', category: 'MLB Futures',
    title: 'World Series Champion 2025',
    description: 'Which team will win the 2025 MLB World Series?',
    volume24h: '$312.4K', totalVolume: '$2.8M',
    options: [
      { name: 'Los Angeles Dodgers', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'New York Yankees', prob: '19%', price: '$0.19', payout: '$1.00', profit: '+$0.81' },
      { name: 'Atlanta Braves', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
      { name: 'Houston Astros', prob: '11%', price: '$0.11', payout: '$1.00', profit: '+$0.89' },
      { name: 'Other', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
    ],
  },
  {
    badge: 'Game Winner', badgeColor: 'teal', category: 'MLB Regular Season',
    title: 'Dodgers vs. Yankees — Moneyline',
    description: 'Who wins the marquee Dodgers vs. Yankees matchup?',
    volume24h: '$187.3K', totalVolume: '$890K',
    options: [
      { name: 'Los Angeles Dodgers', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
      { name: 'New York Yankees', prob: '$0.42', price: '$0.42', payout: '$1.00', profit: '+$0.58' },
    ],
  },
  {
    badge: 'Season Award', badgeColor: 'amber', category: 'MLB Awards',
    title: 'NL Cy Young Award 2025',
    description: 'Which NL pitcher will win the 2025 Cy Young Award?',
    volume24h: '$98.7K', totalVolume: '$487K',
    options: [
      { name: 'Zack Wheeler (PHI)', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
      { name: 'Spencer Strider (ATL)', prob: '27%', price: '$0.27', payout: '$1.00', profit: '+$0.73' },
      { name: 'Logan Webb (SF)', prob: '21%', price: '$0.21', payout: '$1.00', profit: '+$0.79' },
      { name: 'Other', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
    ],
  },
  {
    badge: 'Strikeout Prop', badgeColor: 'cyan', category: 'Pitcher Props',
    title: 'Shohei Ohtani Strikeouts O/U 7.5',
    description: 'Will Shohei Ohtani record over or under 7.5 strikeouts in his next start?',
    volume24h: '$143.6K', totalVolume: '$671K',
    options: [
      { name: 'Over 7.5 Ks', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      { name: 'Under 7.5 Ks', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
    ],
  },
  {
    badge: 'HR Prop', badgeColor: 'rose', category: 'Batter Props',
    title: 'Aaron Judge HR — Yes/No',
    description: 'Will Aaron Judge hit a home run in tonight\'s game?',
    volume24h: '$76.4K', totalVolume: '$341K',
    options: [
      { name: 'Yes — HR', prob: '22%', price: '$0.22', payout: '$1.00', profit: '+$0.78' },
      { name: 'No HR', prob: '78%', price: '$0.78', payout: '$1.00', profit: '+$0.22' },
    ],
  },
  {
    badge: 'Run Total', badgeColor: 'lime', category: 'Totals',
    title: 'Braves vs. Mets O/U 8.5 Runs',
    description: 'Will the combined run total of Braves vs. Mets go over or under 8.5?',
    volume24h: '$54.2K', totalVolume: '$218K',
    options: [
      { name: 'Over 8.5 Runs', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
      { name: 'Under 8.5 Runs', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
    ],
  },
  {
    badge: 'Division Title', badgeColor: 'violet', category: 'MLB Futures',
    title: 'AL East Division Winner',
    description: 'Which team will win the AL East division title in 2025?',
    volume24h: '$112.8K', totalVolume: '$623K',
    options: [
      { name: 'New York Yankees', prob: '41%', price: '$0.41', payout: '$1.00', profit: '+$0.59' },
      { name: 'Baltimore Orioles', prob: '33%', price: '$0.33', payout: '$1.00', profit: '+$0.67' },
      { name: 'Boston Red Sox', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Other', prob: '10%', price: '$0.10', payout: '$1.00', profit: '+$0.90' },
    ],
  },
  {
    badge: 'Hits Prop', badgeColor: 'emerald', category: 'Batter Props',
    title: 'Freddie Freeman Hits O/U 1.5',
    description: 'Will Freddie Freeman record over or under 1.5 hits tonight?',
    volume24h: '$43.7K', totalVolume: '$187K',
    options: [
      { name: 'Over 1.5 Hits', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
      { name: 'Under 1.5 Hits', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
    ],
  },
];

const nhlMarkets: MarketCardProps[] = [
  {
    badge: 'Stanley Cup', badgeColor: 'gold', category: 'NHL Futures',
    title: 'Stanley Cup Champion 2025',
    description: 'Which team will hoist the Stanley Cup in 2025?',
    volume24h: '$198.4K', totalVolume: '$1.7M',
    options: [
      { name: 'Florida Panthers', prob: '24%', price: '$0.24', payout: '$1.00', profit: '+$0.76' },
      { name: 'Colorado Avalanche', prob: '19%', price: '$0.19', payout: '$1.00', profit: '+$0.81' },
      { name: 'Edmonton Oilers', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Boston Bruins', prob: '13%', price: '$0.13', payout: '$1.00', profit: '+$0.87' },
      { name: 'Other', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
    ],
  },
  {
    badge: 'Game Winner', badgeColor: 'teal', category: 'NHL Regular Season',
    title: 'Oilers vs. Avalanche — Moneyline',
    description: 'Who wins the Western Conference showdown between Edmonton and Colorado?',
    volume24h: '$87.3K', totalVolume: '$412K',
    options: [
      { name: 'Colorado Avalanche', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Edmonton Oilers', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
    ],
  },
  {
    badge: 'Season Award', badgeColor: 'amber', category: 'NHL Awards',
    title: 'Hart Trophy 2024-25',
    description: 'Which player will win the Hart Trophy as NHL MVP?',
    volume24h: '$143.7K', totalVolume: '$812K',
    options: [
      { name: 'Connor McDavid (EDM)', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      { name: 'Nathan MacKinnon (COL)', prob: '29%', price: '$0.29', payout: '$1.00', profit: '+$0.71' },
      { name: 'Auston Matthews (TOR)', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
      { name: 'Other', prob: '9%', price: '$0.09', payout: '$1.00', profit: '+$0.91' },
    ],
  },
  {
    badge: 'Goals Prop', badgeColor: 'cyan', category: 'Player Props',
    title: 'McDavid Goals O/U 0.5',
    description: 'Will Connor McDavid score a goal in tonight\'s game?',
    volume24h: '$76.4K', totalVolume: '$341K',
    options: [
      { name: 'Over 0.5 Goals (Yes)', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      { name: 'Under 0.5 Goals (No)', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
    ],
  },
  {
    badge: 'Puck Line', badgeColor: 'rose', category: 'Spread',
    title: 'Panthers -1.5 Puck Line',
    description: 'Will the Panthers win by 2+ goals against the Rangers?',
    volume24h: '$54.8K', totalVolume: '$243K',
    options: [
      { name: 'Panthers -1.5 (Cover)', prob: '41%', price: '$0.41', payout: '$1.00', profit: '+$0.59' },
      { name: 'Rangers +1.5 (Cover)', prob: '59%', price: '$0.59', payout: '$1.00', profit: '+$0.41' },
    ],
  },
  {
    badge: 'Game Total', badgeColor: 'lime', category: 'Totals',
    title: 'Bruins vs. Leafs O/U 5.5 Goals',
    description: 'Will the combined goal total of Bruins vs. Leafs go over or under 5.5?',
    volume24h: '$43.2K', totalVolume: '$198K',
    options: [
      { name: 'Over 5.5 Goals', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      { name: 'Under 5.5 Goals', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
    ],
  },
  {
    badge: 'Assists Prop', badgeColor: 'violet', category: 'Player Props',
    title: 'MacKinnon Assists O/U 1.5',
    description: 'Will Nathan MacKinnon record over or under 1.5 assists tonight?',
    volume24h: '$38.7K', totalVolume: '$167K',
    options: [
      { name: 'Over 1.5 Assists', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
      { name: 'Under 1.5 Assists', prob: '66%', price: '$0.66', payout: '$1.00', profit: '+$0.34' },
    ],
  },
  {
    badge: 'Division Title', badgeColor: 'emerald', category: 'NHL Futures',
    title: 'Atlantic Division Winner',
    description: 'Which team will win the Atlantic Division title in 2024-25?',
    volume24h: '$87.6K', totalVolume: '$423K',
    options: [
      { name: 'Florida Panthers', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      { name: 'Boston Bruins', prob: '29%', price: '$0.29', payout: '$1.00', profit: '+$0.71' },
      { name: 'Toronto Maple Leafs', prob: '21%', price: '$0.21', payout: '$1.00', profit: '+$0.79' },
      { name: 'Other', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
    ],
  },
];

const soccerMarkets: MarketCardProps[] = [
  {
    badge: 'Champions League', badgeColor: 'gold', category: 'UEFA CL Futures',
    title: 'UEFA Champions League Winner',
    description: 'Which club will lift the Champions League trophy this season?',
    volume24h: '$412.8K', totalVolume: '$3.4M',
    options: [
      { name: 'Real Madrid', prob: '27%', price: '$0.27', payout: '$1.00', profit: '+$0.73' },
      { name: 'Manchester City', prob: '21%', price: '$0.21', payout: '$1.00', profit: '+$0.79' },
      { name: 'Bayern Munich', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Arsenal', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Other', prob: '24%', price: '$0.24', payout: '$1.00', profit: '+$0.76' },
    ],
  },
  {
    badge: 'Match Result', badgeColor: 'teal', category: 'Premier League',
    title: 'Arsenal vs. Man City — Result',
    description: 'What will be the result of the Arsenal vs. Manchester City Premier League clash?',
    volume24h: '$287.4K', totalVolume: '$1.4M',
    options: [
      { name: 'Arsenal Win', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      { name: 'Draw', prob: '27%', price: '$0.27', payout: '$1.00', profit: '+$0.73' },
      { name: 'Man City Win', prob: '35%', price: '$0.35', payout: '$1.00', profit: '+$0.65' },
    ],
  },
  {
    badge: 'Top Scorer', badgeColor: 'amber', category: 'Premier League',
    title: 'PL Golden Boot 2024-25',
    description: 'Who will finish as the Premier League top scorer this season?',
    volume24h: '$198.3K', totalVolume: '$1.1M',
    options: [
      { name: 'Erling Haaland (MCI)', prob: '41%', price: '$0.41', payout: '$1.00', profit: '+$0.59' },
      { name: 'Mohamed Salah (LIV)', prob: '24%', price: '$0.24', payout: '$1.00', profit: '+$0.76' },
      { name: 'Cole Palmer (CHE)', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
    ],
  },
  {
    badge: 'Goals Prop', badgeColor: 'cyan', category: 'Player Props',
    title: 'Haaland Goals O/U 0.5',
    description: 'Will Erling Haaland score a goal in the next Premier League match?',
    volume24h: '$143.7K', totalVolume: '$672K',
    options: [
      { name: 'Over 0.5 Goals (Yes)', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Under 0.5 Goals (No)', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
    ],
  },
  {
    badge: 'La Liga', badgeColor: 'rose', category: 'La Liga Futures',
    title: 'La Liga Champion 2024-25',
    description: 'Which club will win the La Liga title this season?',
    volume24h: '$176.4K', totalVolume: '$987K',
    options: [
      { name: 'Real Madrid', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
      { name: 'Barcelona', prob: '31%', price: '$0.31', payout: '$1.00', profit: '+$0.69' },
      { name: 'Atletico Madrid', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Other', prob: '5%', price: '$0.05', payout: '$1.00', profit: '+$0.95' },
    ],
  },
  {
    badge: 'Bundesliga', badgeColor: 'lime', category: 'Bundesliga Futures',
    title: 'Bundesliga Champion 2024-25',
    description: 'Which club will win the Bundesliga title this season?',
    volume24h: '$98.2K', totalVolume: '$512K',
    options: [
      { name: 'Bayern Munich', prob: '58%', price: '$0.58', payout: '$1.00', profit: '+$0.42' },
      { name: 'Bayer Leverkusen', prob: '27%', price: '$0.27', payout: '$1.00', profit: '+$0.73' },
      { name: 'Borussia Dortmund', prob: '10%', price: '$0.10', payout: '$1.00', profit: '+$0.90' },
      { name: 'Other', prob: '5%', price: '$0.05', payout: '$1.00', profit: '+$0.95' },
    ],
  },
  {
    badge: 'Ballon d\'Or', badgeColor: 'violet', category: 'Individual Awards',
    title: 'Ballon d\'Or 2025',
    description: 'Who will win the 2025 Ballon d\'Or award for best player in the world?',
    volume24h: '$234.8K', totalVolume: '$1.6M',
    options: [
      { name: 'Vinicius Jr. (Real Madrid)', prob: '36%', price: '$0.36', payout: '$1.00', profit: '+$0.64' },
      { name: 'Erling Haaland (Man City)', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'Kylian Mbappe (Real Madrid)', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
    ],
  },
  {
    badge: 'Clean Sheet', badgeColor: 'emerald', category: 'Match Props',
    title: 'Liverpool Clean Sheet — Yes/No',
    description: 'Will Liverpool keep a clean sheet in their next Premier League match?',
    volume24h: '$54.3K', totalVolume: '$231K',
    options: [
      { name: 'Yes — Clean Sheet', prob: '41%', price: '$0.41', payout: '$1.00', profit: '+$0.59' },
      { name: 'No Clean Sheet', prob: '59%', price: '$0.59', payout: '$1.00', profit: '+$0.41' },
    ],
  },
];

const tennisMarkets: MarketCardProps[] = [
  {
    badge: 'Grand Slam', badgeColor: 'gold', category: 'Wimbledon 2025',
    title: 'Wimbledon Men\'s Singles Champion',
    description: 'Who will win the Wimbledon Men\'s Singles title in 2025?',
    volume24h: '$198.4K', totalVolume: '$1.2M',
    options: [
      { name: 'Carlos Alcaraz', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
      { name: 'Novak Djokovic', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'Jannik Sinner', prob: '21%', price: '$0.21', payout: '$1.00', profit: '+$0.79' },
      { name: 'Other', prob: '17%', price: '$0.17', payout: '$1.00', profit: '+$0.83' },
    ],
  },
  {
    badge: 'Grand Slam', badgeColor: 'rose', category: 'French Open 2025',
    title: 'French Open Women\'s Champion',
    description: 'Who will win the French Open Women\'s Singles title in 2025?',
    volume24h: '$143.7K', totalVolume: '$812K',
    options: [
      { name: 'Iga Swiatek', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
      { name: 'Aryna Sabalenka', prob: '24%', price: '$0.24', payout: '$1.00', profit: '+$0.76' },
      { name: 'Coco Gauff', prob: '16%', price: '$0.16', payout: '$1.00', profit: '+$0.84' },
      { name: 'Other', prob: '13%', price: '$0.13', payout: '$1.00', profit: '+$0.87' },
    ],
  },
  {
    badge: 'Match Winner', badgeColor: 'teal', category: 'ATP Tour',
    title: 'Alcaraz vs. Sinner — Match Winner',
    description: 'Who wins the highly anticipated Alcaraz vs. Sinner ATP final?',
    volume24h: '$112.3K', totalVolume: '$534K',
    options: [
      { name: 'Carlos Alcaraz', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
      { name: 'Jannik Sinner', prob: '47%', price: '$0.47', payout: '$1.00', profit: '+$0.53' },
    ],
  },
  {
    badge: 'Set Prop', badgeColor: 'cyan', category: 'Match Props',
    title: 'Djokovic Match — Total Sets O/U 3.5',
    description: 'Will Djokovic\'s next match go over or under 3.5 sets?',
    volume24h: '$67.4K', totalVolume: '$298K',
    options: [
      { name: 'Over 3.5 Sets', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      { name: 'Under 3.5 Sets', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
    ],
  },
  {
    badge: 'Year-End #1', badgeColor: 'amber', category: 'ATP Futures',
    title: 'ATP Year-End World No. 1',
    description: 'Who will finish 2025 as the ATP World No. 1 ranked player?',
    volume24h: '$154.8K', totalVolume: '$876K',
    options: [
      { name: 'Jannik Sinner', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
      { name: 'Carlos Alcaraz', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
      { name: 'Novak Djokovic', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Other', prob: '6%', price: '$0.06', payout: '$1.00', profit: '+$0.94' },
    ],
  },
  {
    badge: 'Aces Prop', badgeColor: 'violet', category: 'Match Props',
    title: 'Isner Aces O/U 14.5',
    description: 'Will John Isner serve over or under 14.5 aces in his next match?',
    volume24h: '$32.4K', totalVolume: '$143K',
    options: [
      { name: 'Over 14.5 Aces', prob: '51%', price: '$0.51', payout: '$1.00', profit: '+$0.49' },
      { name: 'Under 14.5 Aces', prob: '49%', price: '$0.49', payout: '$1.00', profit: '+$0.51' },
    ],
  },
];

const golfMarkets: MarketCardProps[] = [
  {
    badge: 'Major', badgeColor: 'gold', category: 'The Masters 2025',
    title: 'The Masters Champion 2025',
    description: 'Who will win the green jacket at The Masters Tournament in 2025?',
    volume24h: '$287.4K', totalVolume: '$2.1M',
    options: [
      { name: 'Scottie Scheffler', prob: '28%', price: '$0.28', payout: '$1.00', profit: '+$0.72' },
      { name: 'Rory McIlroy', prob: '18%', price: '$0.18', payout: '$1.00', profit: '+$0.82' },
      { name: 'Jon Rahm', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
      { name: 'Xander Schauffele', prob: '11%', price: '$0.11', payout: '$1.00', profit: '+$0.89' },
      { name: 'Other', prob: '29%', price: '$0.29', payout: '$1.00', profit: '+$0.71' },
    ],
  },
  {
    badge: 'Major', badgeColor: 'teal', category: 'US Open 2025',
    title: 'US Open Champion 2025',
    description: 'Who will win the 2025 US Open golf championship?',
    volume24h: '$198.3K', totalVolume: '$1.4M',
    options: [
      { name: 'Scottie Scheffler', prob: '24%', price: '$0.24', payout: '$1.00', profit: '+$0.76' },
      { name: 'Rory McIlroy', prob: '19%', price: '$0.19', payout: '$1.00', profit: '+$0.81' },
      { name: 'Collin Morikawa', prob: '13%', price: '$0.13', payout: '$1.00', profit: '+$0.87' },
      { name: 'Other', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
    ],
  },
  {
    badge: 'Tournament', badgeColor: 'amber', category: 'PGA Tour',
    title: 'Players Championship Winner',
    description: 'Who will win THE PLAYERS Championship at TPC Sawgrass?',
    volume24h: '$143.7K', totalVolume: '$812K',
    options: [
      { name: 'Scottie Scheffler', prob: '21%', price: '$0.21', payout: '$1.00', profit: '+$0.79' },
      { name: 'Xander Schauffele', prob: '14%', price: '$0.14', payout: '$1.00', profit: '+$0.86' },
      { name: 'Rory McIlroy', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Other', prob: '53%', price: '$0.53', payout: '$1.00', profit: '+$0.47' },
    ],
  },
  {
    badge: 'Scoring Prop', badgeColor: 'cyan', category: 'Round Props',
    title: 'Scheffler Round 1 Score O/U 68.5',
    description: 'Will Scottie Scheffler shoot over or under 68.5 in Round 1?',
    volume24h: '$76.4K', totalVolume: '$341K',
    options: [
      { name: 'Under 68.5 (Better)', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Over 68.5 (Worse)', prob: '46%', price: '$0.46', payout: '$1.00', profit: '+$0.54' },
    ],
  },
  {
    badge: 'Ryder Cup', badgeColor: 'rose', category: 'Team Events',
    title: 'Ryder Cup 2025 Winner',
    description: 'Will Team USA or Team Europe win the 2025 Ryder Cup?',
    volume24h: '$112.8K', totalVolume: '$623K',
    options: [
      { name: 'Team USA', prob: '56%', price: '$0.56', payout: '$1.00', profit: '+$0.44' },
      { name: 'Team Europe', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
    ],
  },
  {
    badge: 'HIO Prop', badgeColor: 'violet', category: 'Tournament Props',
    title: 'Hole-in-One at The Masters',
    description: 'Will any player make a hole-in-one during The Masters Tournament?',
    volume24h: '$43.2K', totalVolume: '$198K',
    options: [
      { name: 'Yes — Hole-in-One', prob: '31%', price: '$0.31', payout: '$1.00', profit: '+$0.69' },
      { name: 'No Hole-in-One', prob: '69%', price: '$0.69', payout: '$1.00', profit: '+$0.31' },
    ],
  },
];

const mmaMarkets: MarketCardProps[] = [
  {
    badge: 'Title Fight', badgeColor: 'gold', category: 'UFC Heavyweight',
    title: 'UFC Heavyweight Championship',
    description: 'Who will hold the UFC Heavyweight title after the next championship bout?',
    volume24h: '$312.4K', totalVolume: '$2.3M',
    options: [
      { name: 'Jon Jones (Retain)', prob: '61%', price: '$0.61', payout: '$1.00', profit: '+$0.39' },
      { name: 'Stipe Miocic (Win)', prob: '39%', price: '$0.39', payout: '$1.00', profit: '+$0.61' },
    ],
  },
  {
    badge: 'Fight Winner', badgeColor: 'rose', category: 'UFC Main Event',
    title: 'Pereira vs. Hill — Fight Winner',
    description: 'Who wins the UFC Light Heavyweight title fight between Pereira and Hill?',
    volume24h: '$198.7K', totalVolume: '$1.1M',
    options: [
      { name: 'Alex Pereira', prob: '64%', price: '$0.64', payout: '$1.00', profit: '+$0.36' },
      { name: 'Jamahal Hill', prob: '36%', price: '$0.36', payout: '$1.00', profit: '+$0.64' },
    ],
  },
  {
    badge: 'Method of Victory', badgeColor: 'amber', category: 'Fight Props',
    title: 'Pereira vs. Hill — Method',
    description: 'How will the Pereira vs. Hill fight end?',
    volume24h: '$143.6K', totalVolume: '$671K',
    options: [
      { name: 'KO/TKO', prob: '54%', price: '$0.54', payout: '$1.00', profit: '+$0.46' },
      { name: 'Submission', prob: '12%', price: '$0.12', payout: '$1.00', profit: '+$0.88' },
      { name: 'Decision', prob: '34%', price: '$0.34', payout: '$1.00', profit: '+$0.66' },
    ],
  },
  {
    badge: 'Round Prop', badgeColor: 'cyan', category: 'Fight Props',
    title: 'Pereira vs. Hill — Goes to Rd 3?',
    description: 'Will the Pereira vs. Hill fight last into Round 3 or beyond?',
    volume24h: '$87.4K', totalVolume: '$398K',
    options: [
      { name: 'Yes — Reaches Rd 3', prob: '43%', price: '$0.43', payout: '$1.00', profit: '+$0.57' },
      { name: 'No — Ends Before Rd 3', prob: '57%', price: '$0.57', payout: '$1.00', profit: '+$0.43' },
    ],
  },
  {
    badge: 'Title Fight', badgeColor: 'teal', category: 'UFC Welterweight',
    title: 'UFC Welterweight Championship',
    description: 'Who will hold the UFC Welterweight title after the next championship bout?',
    volume24h: '$176.3K', totalVolume: '$987K',
    options: [
      { name: 'Leon Edwards (Retain)', prob: '48%', price: '$0.48', payout: '$1.00', profit: '+$0.52' },
      { name: 'Belal Muhammad (Win)', prob: '52%', price: '$0.52', payout: '$1.00', profit: '+$0.48' },
    ],
  },
  {
    badge: 'Fight Winner', badgeColor: 'violet', category: 'UFC Co-Main',
    title: 'Adesanya vs. Strickland 3',
    description: 'Who wins the trilogy fight between Israel Adesanya and Sean Strickland?',
    volume24h: '$154.8K', totalVolume: '$876K',
    options: [
      { name: 'Israel Adesanya', prob: '57%', price: '$0.57', payout: '$1.00', profit: '+$0.43' },
      { name: 'Sean Strickland', prob: '43%', price: '$0.43', payout: '$1.00', profit: '+$0.57' },
    ],
  },
  {
    badge: 'Knockout Prop', badgeColor: 'lime', category: 'Fight Props',
    title: 'Ngannou Next Fight — KO Win?',
    description: 'Will Francis Ngannou win his next fight by KO/TKO?',
    volume24h: '$98.4K', totalVolume: '$441K',
    options: [
      { name: 'Yes — KO/TKO Win', prob: '44%', price: '$0.44', payout: '$1.00', profit: '+$0.56' },
      { name: 'No KO Win', prob: '56%', price: '$0.56', payout: '$1.00', profit: '+$0.44' },
    ],
  },
  {
    badge: 'Event Prop', badgeColor: 'emerald', category: 'UFC Event',
    title: 'UFC 300 — Main Event Finish?',
    description: 'Will the UFC 300 main event end by finish (KO/TKO/Sub) or go to decision?',
    volume24h: '$67.3K', totalVolume: '$289K',
    options: [
      { name: 'Finish (KO/TKO/Sub)', prob: '62%', price: '$0.62', payout: '$1.00', profit: '+$0.38' },
      { name: 'Goes to Decision', prob: '38%', price: '$0.38', payout: '$1.00', profit: '+$0.62' },
    ],
  },
];

// ─── Tab Config ──────────────────────────────────────────────────────────────

const TABS = [
  { key: 'nfl', label: 'NFL', icon: 'ri-football-line', color: 'text-amber-400', accentColor: 'amber', data: nflMarkets, filters: ['All', 'Game Winner', 'Point Spread', 'Game Total', 'Player Prop', 'Season Award', 'Playoff Seed', 'Rushing Prop', 'Receiving Prop'] },
  { key: 'nba', label: 'NBA', icon: 'ri-basketball-line', color: 'text-orange-400', accentColor: 'orange', data: nbaMarkets, filters: ['All', 'Game Winner', 'Championship', 'Season Award', 'Player Prop', 'Point Spread', 'Game Total', 'Assists Prop', 'Rebounds Prop'] },
  { key: 'mlb', label: 'MLB', icon: 'ri-baseball-line', color: 'text-red-400', accentColor: 'red', data: mlbMarkets, filters: ['All', 'World Series', 'Game Winner', 'Season Award', 'Strikeout Prop', 'HR Prop', 'Run Total', 'Division Title', 'Hits Prop'] },
  { key: 'nhl', label: 'NHL', icon: 'ri-ice-cream-line', color: 'text-cyan-400', accentColor: 'cyan', data: nhlMarkets, filters: ['All', 'Stanley Cup', 'Game Winner', 'Season Award', 'Goals Prop', 'Puck Line', 'Game Total', 'Assists Prop', 'Division Title'] },
  { key: 'soccer', label: 'Soccer', icon: 'ri-football-line', color: 'text-emerald-400', accentColor: 'emerald', data: soccerMarkets, filters: ['All', 'Champions League', 'Match Result', 'Top Scorer', 'Goals Prop', 'La Liga', 'Bundesliga', 'Ballon d\'Or', 'Clean Sheet'] },
  { key: 'tennis', label: 'Tennis', icon: 'ri-ping-pong-line', color: 'text-lime-400', accentColor: 'lime', data: tennisMarkets, filters: ['All', 'Grand Slam', 'Match Winner', 'Set Prop', 'Year-End #1', 'Aces Prop'] },
  { key: 'golf', label: 'Golf', icon: 'ri-flag-line', color: 'text-green-400', accentColor: 'green', data: golfMarkets, filters: ['All', 'Major', 'Tournament', 'Scoring Prop', 'Ryder Cup', 'HIO Prop'] },
  { key: 'mma', label: 'MMA', icon: 'ri-boxing-line', color: 'text-rose-400', accentColor: 'rose', data: mmaMarkets, filters: ['All', 'Title Fight', 'Fight Winner', 'Method of Victory', 'Round Prop', 'Knockout Prop', 'Event Prop'] },
];

const accentMap: Record<string, { badge: string; border: string; text: string; bg: string }> = {
  amber:   { badge: 'bg-amber-500/20 text-amber-400 border-amber-500/30',   border: 'border-amber-500/50',   text: 'text-amber-400',   bg: 'bg-amber-500/10' },
  orange:  { badge: 'bg-orange-500/20 text-orange-400 border-orange-500/30', border: 'border-orange-500/50',  text: 'text-orange-400',  bg: 'bg-orange-500/10' },
  red:     { badge: 'bg-red-500/20 text-red-400 border-red-500/30',          border: 'border-red-500/50',     text: 'text-red-400',     bg: 'bg-red-500/10' },
  cyan:    { badge: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',       border: 'border-cyan-500/50',    text: 'text-cyan-400',    bg: 'bg-cyan-500/10' },
  emerald: { badge: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', border: 'border-emerald-500/50', text: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  lime:    { badge: 'bg-lime-500/20 text-lime-400 border-lime-500/30',       border: 'border-lime-500/50',    text: 'text-lime-400',    bg: 'bg-lime-500/10' },
  green:   { badge: 'bg-green-500/20 text-green-400 border-green-500/30',    border: 'border-green-500/50',   text: 'text-green-400',   bg: 'bg-green-500/10' },
  rose:    { badge: 'bg-rose-500/20 text-rose-400 border-rose-500/30',       border: 'border-rose-500/50',    text: 'text-rose-400',    bg: 'bg-rose-500/10' },
};

const statsMap: Record<string, { markets: string; vol24h: string; totalVol: string; categories: string }> = {
  nfl:    { markets: '84', vol24h: '$4.2M', totalVol: '$28.7M', categories: '9' },
  nba:    { markets: '71', vol24h: '$3.1M', totalVol: '$21.4M', categories: '8' },
  mlb:    { markets: '63', vol24h: '$2.4M', totalVol: '$16.8M', categories: '8' },
  nhl:    { markets: '52', vol24h: '$1.8M', totalVol: '$12.3M', categories: '8' },
  soccer: { markets: '96', vol24h: '$5.7M', totalVol: '$38.2M', categories: '8' },
  tennis: { markets: '44', vol24h: '$1.2M', totalVol: '$8.4M',  categories: '6' },
  golf:   { markets: '38', vol24h: '$0.9M', totalVol: '$6.1M',  categories: '6' },
  mma:    { markets: '29', vol24h: '$1.4M', totalVol: '$9.8M',  categories: '8' },
};

const SUBNAV = [
  { key: 'all', label: 'All', icon: 'ri-apps-2-line', path: '/markets' },
  { key: 'spreads', label: 'Spreads', icon: 'ri-bar-chart-grouped-line', path: '/markets/spreads' },
  { key: 'money-lines', label: 'Money Lines', icon: 'ri-copper-coin-line', path: '/markets/money-lines' },
  { key: 'futures', label: 'Futures', icon: 'ri-calendar-event-line', path: '/markets/futures' },
  { key: 'parlays', label: 'Parlays', icon: 'ri-stack-line', path: '/markets/parlays' },
  { key: 'player-props', label: 'Player Props', icon: 'ri-user-star-line', path: '/markets/player-props' },
];

// Badge-to-subNav mapping
const BADGE_SUBNAV_MAP: Record<string, string> = {
  'Point Spread': 'spreads',
  'Puck Line': 'spreads',
  'Game Winner': 'money-lines',
  'Match Result': 'money-lines',
  'Fight Winner': 'money-lines',
  'World Series': 'futures',
  'Stanley Cup': 'futures',
  'Champions League': 'futures',
  'Championship': 'futures',
  'Season Award': 'futures',
  'Playoff Seed': 'futures',
  'Division Title': 'futures',
  'Year-End #1': 'futures',
  'Major': 'futures',
  'Tournament': 'futures',
  'Ryder Cup': 'futures',
  'Title Fight': 'futures',
  'La Liga': 'futures',
  'Bundesliga': 'futures',
  'Ballon d\'Or': 'futures',
  'Grand Slam': 'futures',
  'Player Prop': 'player-props',
  'Rushing Prop': 'player-props',
  'Receiving Prop': 'player-props',
  'Assists Prop': 'player-props',
  'Rebounds Prop': 'player-props',
  'Strikeout Prop': 'player-props',
  'HR Prop': 'player-props',
  'Hits Prop': 'player-props',
  'Goals Prop': 'player-props',
  'Set Prop': 'player-props',
  'Aces Prop': 'player-props',
  'Scoring Prop': 'player-props',
  'Round Prop': 'player-props',
  'Knockout Prop': 'player-props',
  'Method of Victory': 'player-props',
  'Clean Sheet': 'player-props',
  'HIO Prop': 'player-props',
  'Top Scorer': 'player-props',
};

// ─── Page ────────────────────────────────────────────────────────────────────

export default function MarketsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tabParam = searchParams.get('tab') || 'nfl';
  const activeTab = TABS.find(t => t.key === tabParam) || TABS[0];

  const accent = accentMap[activeTab.accentColor] || accentMap['amber'];
  const stats = statsMap[activeTab.key];

  const [activeFilter, setActiveFilter] = useState('All');
  const [activeSubNav, setActiveSubNav] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [slipCount, setSlipCount] = useState(0);
  const [marketsDropdownOpen, setMarketsDropdownOpen] = useState(false);
  const marketsDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (marketsDropdownRef.current && !marketsDropdownRef.current.contains(e.target as Node)) {
        setMarketsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine active sub-nav based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/markets') {
      setActiveSubNav('all');
    } else if (path === '/markets/spreads') {
      setActiveSubNav('spreads');
    } else if (path === '/markets/money-lines') {
      setActiveSubNav('money-lines');
    } else if (path === '/markets/futures') {
      setActiveSubNav('futures');
    } else if (path === '/markets/parlays') {
      setActiveSubNav('parlays');
    } else if (path === '/markets/player-props') {
      setActiveSubNav('player-props');
    }
  }, [location.pathname]);

  // Filter by badge filter first, then by sub-nav
  const filteredByBadge = activeFilter === 'All'
    ? activeTab.data
    : activeTab.data.filter(m => m.badge === activeFilter);

  const filtered = activeSubNav === 'all'
    ? filteredByBadge
    : filteredByBadge.filter(m => {
        if (activeSubNav === 'parlays') return false; // parlays shown as empty/coming soon
        return BADGE_SUBNAV_MAP[m.badge] === activeSubNav;
      });

  const handleTabChange = (key: string) => {
    setActiveFilter('All');
    setActiveSubNav('all');
    navigate(`/markets?tab=${key}`);
  };

  useEffect(() => {
    setActiveFilter('All');
    setActiveSubNav('all');
  }, [tabParam]);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-markets';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = schemaId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `L2L ${activeTab.label} Prediction Markets`,
      url: `${siteUrl}/markets?tab=${activeTab.key}`,
      description: `Trade ${activeTab.label} outcomes peer-to-peer with zero juice on L2L, the CFTC regulated sports prediction market. Available in all 50 states.`,
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, [activeTab.key, activeTab.label]);

  return (
    <div className="min-h-screen" style={{ background: '#2b2b2b' }}>
      {/* Demo Banner */}
      <div className="bg-amber-500 text-slate-900 px-4 py-2 relative z-[100] shadow-md">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-center">
          <i className="ri-alert-line text-lg"></i>
          <span className="font-bold text-sm md:text-base tracking-wide uppercase">
            Demo Only — Experience the Future of Sports Betting — From Sportsbook to Prediction Market
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg border-b border-slate-700/50 transition-colors duration-300" style={{ background: 'rgba(43,43,43,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 gap-4">
            <a className="flex items-center gap-2 flex-shrink-0" href="/">
              <div className="h-11 w-44 flex items-center justify-center">
                <img
                  src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                  alt="RTJ Logo"
                  className="h-full w-full object-contain"
                />
              </div>
            </a>
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
              <a href="/"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/60 hover:text-white"><i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>Home</div></a>
              <a href="/march-madness"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/60 hover:text-white"><i className="ri-basketball-line w-4 h-4 flex items-center justify-center"></i>March Madness</div></a>
              <a href="/super-bowl-props"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/60 hover:text-white"><i className="ri-football-line w-4 h-4 flex items-center justify-center"></i>Super Bowl</div></a>
              <div className="relative" ref={marketsDropdownRef}>
                <button
                  onClick={() => setMarketsDropdownOpen(!marketsDropdownOpen)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 bg-teal-500/20 text-teal-400"
                >
                  <i className="ri-bar-chart-2-line w-4 h-4 flex items-center justify-center"></i>
                  Markets
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${marketsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {marketsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-52 rounded-xl border border-slate-700/80 shadow-2xl overflow-hidden z-50" style={{ background: 'rgba(30,30,30,0.98)' }}>
                    <div className="px-3 py-2 border-b border-slate-700/50">
                      <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Market Types</span>
                    </div>
                    {[
                      { label: 'Spreads',      icon: 'ri-bar-chart-grouped-line', color: 'text-teal-400',    path: '/markets/spreads' },
                      { label: 'Money Lines',  icon: 'ri-copper-coin-line',       color: 'text-amber-400',   path: '/markets/money-lines' },
                      { label: 'Futures',      icon: 'ri-calendar-event-line',    color: 'text-emerald-400', path: '/markets/futures' },
                      { label: 'Parlays',      icon: 'ri-stack-line',             color: 'text-violet-400',  path: '/markets/parlays' },
                      { label: 'Player Props', icon: 'ri-user-star-line',         color: 'text-cyan-400',    path: '/markets/player-props' },
                    ].map(item => (
                      <a key={item.path} href={item.path} onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center gap-3 px-4 py-2.5 text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors cursor-pointer">
                          <i className={`${item.icon} ${item.color} text-base w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm font-medium">{item.label}</span>
                        </div>
                      </a>
                    ))}
                    <div className="border-t border-slate-700/50 px-3 py-2">
                      <a href="/markets" onClick={() => setMarketsDropdownOpen(false)}>
                        <div className="flex items-center justify-between text-teal-400 hover:text-teal-300 transition-colors cursor-pointer text-sm font-semibold py-1">
                          <span>All Markets</span>
                          <i className="ri-arrow-right-line"></i>
                        </div>
                      </a>
                    </div>
                  </div>
                )}
              </div>
              <a href="/why-rtj"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/50 hover:text-white"><i className="ri-question-line w-4 h-4 flex items-center justify-center"></i>Why L2L</div></a>
              <a href="/my-account"><div className="px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 text-slate-300 hover:bg-slate-800/60 hover:text-white"><i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>My Account</div></a>

            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-500/20 to-teal-600/10 rounded-lg border border-teal-500/30">
                <i className="ri-wallet-3-line text-teal-400 text-sm"></i>
                <span className="font-semibold text-white text-sm whitespace-nowrap">$10,000</span>
              </div>
              <button onClick={() => setSlipCount(c => c + 1)} className="relative p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors cursor-pointer" title="Bet Slip">
                <i className="ri-file-list-3-line text-teal-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                {slipCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-teal-500 text-white text-xs rounded-full flex items-center justify-center font-bold">{slipCount}</span>}
              </button>
              <a href="/my-account"><div className="p-2 rounded-lg transition-colors cursor-pointer bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700"><i className="ri-user-line text-lg w-5 h-5 flex items-center justify-center"></i></div></a>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-slate-300 hover:text-white hover:bg-slate-800/50 rounded-lg transition-colors cursor-pointer" aria-label="Toggle menu">
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-slate-700/50 py-3 space-y-1">
              {[
                { href: '/', icon: 'ri-home-line', label: 'Home' },
                { href: '/march-madness', icon: 'ri-basketball-line', label: 'March Madness' },
                { href: '/super-bowl-props', icon: 'ri-football-line', label: 'Super Bowl' },
                { href: '/markets', icon: 'ri-bar-chart-2-line', label: 'Markets' },
                { href: '/why-rtj', icon: 'ri-question-line', label: 'Why L2L' },
                { href: '/my-account', icon: 'ri-user-line', label: 'My Account' },
              ].map(item => (
                <a key={item.href} href={item.href}>
                  <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-300 hover:bg-slate-800/50 hover:text-white transition-colors cursor-pointer">
                    <i className={`${item.icon} text-base`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero */}
      <PageHero
        badgeLabel="Live Markets"
        badgeColor="teal"
        title="All Sports Markets"
        subtitle="Trade outcomes across NFL, NBA, MLB, NHL, Soccer, Tennis, Golf & MMA. Peer-to-peer, zero juice, CFTC regulated."
        backgroundImage="https://public.readdy.ai/ai/img_res/7e8de3bc86f754ddbc5267dde19f9b28.jpg"
      />

      {/* Sport Tabs */}
      <div className="sticky top-[57px] z-40 border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.97)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeTab.key === tab.key
                    ? `bg-teal-500/20 text-teal-400 border border-teal-500/40`
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border border-transparent'
                }`}
              >
                <i className={`${tab.icon} ${activeTab.key === tab.key ? 'text-teal-400' : tab.color}`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sub Navigation */}
      <div className="sticky top-[105px] z-[39] border-b border-slate-700/50" style={{ background: 'rgba(22,22,22,0.98)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-none py-2">
            {SUBNAV.map(sub => (
              <a key={sub.key} href={sub.path}>
                <button
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    activeSubNav === sub.key
                      ? 'bg-slate-700 text-white border border-slate-500/60'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent'
                  }`}
                >
                  <i className={`${sub.icon} text-sm ${activeSubNav === sub.key ? 'text-teal-400' : 'text-slate-500'}`}></i>
                  {sub.label}
                </button>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="border-b border-slate-700/50" style={{ background: 'rgba(30,30,30,0.8)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { label: 'Active Markets', value: stats.markets, icon: 'ri-bar-chart-2-line', color: 'text-teal-400' },
              { label: '24h Volume', value: stats.vol24h, icon: 'ri-line-chart-line', color: 'text-emerald-400' },
              { label: 'Total Volume', value: stats.totalVol, icon: 'ri-water-flash-line', color: accent.text },
              { label: 'Categories', value: stats.categories, icon: activeTab.icon, color: activeTab.color },
            ].map((stat, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <i className={`${stat.icon} text-xl ${stat.color}`}></i>
                <div className={`text-xl font-extrabold ${stat.color}`}>{stat.value}</div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Markets Section */}
      <section className="py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-8">
            <div>
              <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white flex items-center gap-3">
                <i className={`${activeTab.icon} ${activeTab.color}`}></i>
                {activeTab.label} {activeSubNav !== 'all' ? SUBNAV.find(s => s.key === activeSubNav)?.label : 'Markets'}
              </h2>
              <p className="text-slate-400 text-sm mt-1">Trade on every outcome — futures, game lines, and player props</p>
            </div>
            <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 flex-nowrap sm:flex-wrap sm:justify-end sm:max-w-xs lg:max-w-sm flex-shrink-0">
              {activeTab.filters.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    activeFilter === f
                      ? 'bg-teal-500/20 border-teal-500/50 text-teal-400'
                      : 'bg-slate-800/60 border-slate-700/50 text-slate-400 hover:border-slate-500 hover:text-slate-300'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {activeSubNav === 'parlays' ? (
            <div className="text-center py-24 text-slate-500">
              <i className="ri-stack-line text-5xl mb-4 block text-slate-600"></i>
              <p className="text-lg font-semibold text-slate-400 mb-2">Parlays Coming Soon</p>
              <p className="text-sm text-slate-600 max-w-sm mx-auto">Multi-leg parlay markets are on the way. Check back soon for combined outcome trading.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {filtered.map((market, idx) => (
                  <MarketCard key={idx} {...market} />
                ))}
              </div>

              {filtered.length === 0 && (
                <div className="text-center py-20 text-slate-500">
                  <i className="ri-search-line text-4xl mb-3 block"></i>
                  <p>No markets found for this filter.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl border border-teal-500/20 p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(20,184,166,0.08) 0%, rgba(43,43,43,0.9) 50%, rgba(20,184,166,0.05) 100%)' }}>
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500/5 via-transparent to-emerald-500/5"></div>
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Trade?</h3>
              <p className="text-slate-400 mb-6 max-w-xl mx-auto">No house edge. No bet limits. Pure market-driven pricing on every outcome.</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="/super-bowl-props">
                  <button className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-gradient-to-r from-teal-400 to-emerald-400 hover:from-teal-500 hover:to-emerald-500 text-slate-900 font-bold rounded-lg shadow-lg shadow-teal-500/25 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-football-line"></i>
                    Super Bowl Markets
                  </button>
                </a>
                <a href="/march-madness">
                  <button className="inline-flex items-center justify-center gap-2 px-7 py-3 border border-slate-600 text-slate-300 hover:bg-slate-800 hover:text-white rounded-lg transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-basketball-line"></i>
                    March Madness
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 py-8 px-4" style={{ background: '#1e1e1e' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-40 flex items-center justify-center">
              <img
                src="https://static.readdy.ai/image/bc21bbd943df20a73e2c62e0eb37a79f/356d7ca1b6cdb5f780c70d0d0bd7c02d.png"
                alt="Liability to Liquidity Logo"
                className="h-full w-full object-contain"
              />
            </div>
          </div>
          <p className="text-slate-600 text-xs text-center">Demo only. Not real money. For educational purposes only. Must be 18+ to participate.</p>
          <div className="flex items-center gap-4 text-slate-500 text-xs">
            <a href="/" className="hover:text-slate-300 transition-colors cursor-pointer">Home</a>
            <a href="/super-bowl-props" className="hover:text-slate-300 transition-colors cursor-pointer">Super Bowl</a>
            <a href="/march-madness" className="hover:text-slate-300 transition-colors cursor-pointer">March Madness</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
